import { message } from '@tencent/tea-component';
import { TicketData } from '@tencent/wemeet-js-sdk';
import { setCookie } from '@utils/browser/get-cookie';
import isServer from '@utils/env/is-server';
import { goLogin } from '@utils/login-util';
import cookies from 'next-cookies';
import Axios, { AxiosRequestConfig } from 'axios';
import { GetServerSidePropsContext } from 'next';
import { NextRequest } from 'next/server';
import getHost from '../../utils/get-host';
import { COOKIE_TDE_PROXY, COOKIE_TDE_PROXY_TRUE } from '@constant/tde-proxy';
import nodeAegisInfoAll, {
  nodeAegisInfo,
  nodeAegisReportError,
  nodeAegisReportEvent,
  nodeAegisReportTime,
} from '../node-aegis';
import { getReqConfig } from './get-req-config';
import getUrl from './get-url';

const BASE_URL = '/';
const DISABLED_TICKET_CODE = -160000;
const DISABLED_TOKEN_CODE = -51003;

const http = Axios.create({
  timeout: 20 * 1000,
});

http.interceptors.request.use((config) => {
  const axiosConfig = config;
  axiosConfig.baseURL = BASE_URL;
  // axiosConfig.withCredentials = true;
  // axiosConfig.proxy = false; // 这里用于本地调试
  axiosConfig.headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    ...config.headers,
  };
  return axiosConfig;
});

// 业务逻辑是否存在异常
// 不同接口的返回码不一样，需要枚举兼容
const isLogicSuccess = (response: any) =>
  response?.data?.code === 0 || response?.data?.code === 200;

// 签名不合法时重新手动拉取ticket
const resetTicket = async (params: any, response: any, type: string) => {
  try {
    let ticketType = '';
    const code = parseInt(response.data?.code, 10);
    if (code === DISABLED_TICKET_CODE || code === DISABLED_TOKEN_CODE || response.status === 401) {
      ticketType = 'W10';
    }

    if (ticketType) {
      console.log('resetTicket ticketType', ticketType);
      const iswemeet = typeof wemeet !== 'undefined';
      console.log('resetTicket iswemeet', iswemeet);
      if (iswemeet) {
        const { data, app } = wemeet;
        const support = (await app.compare('2.3.0')) > -1;
        console.log('resetTicket support', support);
        if (!support) {
          // toast(`客户端版本低，请先升级版本再尝试(${DISABLED_TICKET_CODE - 2})`);
        } else {
          const paramsData = JSON.parse(JSON.stringify(params));
          const ticResult: TicketData = await data.getTicket({ type: 'W10' });
          console.log('resetTicket ticResult', ticResult!);
          if (ticResult! && ticResult.ticket) {
            const { ticket } = ticResult;
            // if (paramsData.data) {
            //   paramsData.data.ticket = ticket;
            // }
            if (!isServer) {
              window.currentTicket = ticket;
              // 设置cookie，重新发送请求
              setCookie('ticket', ticket, 0.05, '/');
            }
            if (!paramsData.data?.isRefresh) {
              if (!paramsData.data) {
                paramsData.data = {};
              }
              paramsData.data.isRefresh = true;
              if (type === 'post') {
                const res = post(
                  paramsData.prefix,
                  paramsData.path,
                  paramsData.data,
                  paramsData.ctx,
                  paramsData.query,
                );
                return res;
              }
              if (type === 'get') {
                const res = post(
                  paramsData.prefix,
                  paramsData.path,
                  paramsData.query,
                  paramsData.ctx,
                );
                return res;
              }
            } else {
              // toast(`网络波动，请重试(${DISABLED_TICKET_CODE - 4})`);
            }
          } else {
            // toast(`网络波动，请重试(${DISABLED_TICKET_CODE - 3})`);
          }
        }
      } else {
        if (response.status === 401) {
          goLogin();
          message.error({ content: '请先登录' });
        } else {
          return {
            code: 401,
            data: response.data,
          };
        }
      }
    }
    return response.data;
  } catch (e) {
    window.nextAegis.report('resetTicket response.data', e);
    console.log('response.data', e);
    return response.data;
  }
};

export const post = async <T, D extends {} = {}>(
  prefix: any,
  path: string,
  data: D,
  ctx?: GetServerSidePropsContext,
  query: Record<string, string | undefined> = {},
  req?: NextRequest,
  clearTdeCookie?: boolean,
) => {
  const params = {
    prefix,
    path,
    data,
    ctx,
    query,
  };

  const config = await getReqConfig(ctx, req, clearTdeCookie);
  config.params = {
    ...config.params,
    ...query,
  };

  const allCookies = cookies(ctx || {});

  const { url, callback } = await getUrl({
    prefix,
    path,
    hostName: ctx && getHost(ctx),
    tdeProxy: allCookies[COOKIE_TDE_PROXY] === COOKIE_TDE_PROXY_TRUE,
    usePolaris: true,
    ctx,
  });
  const reportPath = prefix + path;
  // 测速上报：服务端单独上报，用户端Aegis全局托管请求监控，已全量上报
  // 测速打点开始
  const startTime = new Date().getTime();
  let response: T;
  if (isServer && !ctx) {
    response = await sendFetchRequest('POST', url, data, config).catch((e) => {
      callback(false, 0, '-1');
      // 错误统一上报
      reportHttpError(e, ctx, url, config, reportPath);
      if (e?.response?.status === 401) {
        return e.response;
      }
      return Promise.reject(e);
    });
  } else {
    response = await http
      .post<T>(url, data, config)
      .then((res) => {
        // 错误需要上报一下
        // @ts-ignore
        if (!(res?.data && res.data.code === 0)) {
          reportHttpError(res.data, ctx, url, config, reportPath);
        }
        return res;
      })
      .catch((e) => {
        callback(false, 0, '-1');
        // 错误统一上报
        reportHttpError(e, ctx, url, config, reportPath);
        if (e?.response?.status === 401) {
          return e.response;
        }
        return Promise.reject(`服务请求出错——[url: ${url}]`);
      });
  }

  // 测速打点结束
  const httpDuration = new Date().getTime() - startTime;
  callback(true, httpDuration, '0');
  ctx && nodeAegisReportTime(reportPath, httpDuration, ctx);

  if (ctx) {
    const reportLogData = `
      【method】: post,
      【url】: ${url},
      【httpDuration】:${httpDuration},
      【status】: ${JSON.stringify(response.status)},
      【response】: ${JSON.stringify({ ...response.data, data: {} })}`;
    // 上报日志需要脱敏
    // 【request】: ${JSON.stringify(data)},
    // 【params】:${JSON.stringify(response?.config?.params || {})},
    // 【response】: ${JSON.stringify(response.data)}`;

    // 业务逻辑错误
    if (!isLogicSuccess(response)) {
      nodeAegisInfoAll(`Response Logic Error - ${path} :${reportLogData}`, ctx);
      // 统计服务端业务错误数量
      nodeAegisReportEvent(`${response.data.code}_${response.data.message}`, ctx);
    }
    // 全量返回日志
    nodeAegisInfoAll(`Response : ${reportLogData}`, ctx);
    // 白名单日志上报
    nodeAegisInfo(
      `Response - Detail :
      ${reportLogData},
      [cookie]:${ctx?.req?.headers?.cookie}`,
      ctx,
    );
  }

  if (!isServer) {
    // 检测签名不合法重新获取
    response.data = await resetTicket(params, response, 'post');
  } else if (response.status !== 200) {
    // 服务端错误。
    return response;
  }
  return response.data as T;
};

const sendFetchRequest = async (method: string, url: string, data: any, config: any) => {
  const obj2strUrl = (obj: any) => {
    let str = '';
    Object.keys(obj).forEach((key) => {
      str = `${str}${key}=${encodeURIComponent(obj[key])}&`;
    });
    if (str) {
      str = str.substring(0, str.length - 1);
    }
    return str;
  };
  let queryUrl = url;
  if (config?.params) {
    queryUrl = `${url}?${obj2strUrl(config.params)}`;
  }
  console.log('sendFetchRequest url', queryUrl);
  const headers = {
    ...config.headers,
    'Content-Type': 'application/json',
  };
  console.log('sendFetchRequest headers', headers);
  return await fetch(queryUrl, {
    method,
    headers,
    body: JSON.stringify(data),
  }).then(async (response) => {
    const data = await response.json();
    return {
      status: response.status,
      data,
      config,
    };
  });
};

export const get = async <T, D extends {} = {}>(
  prefix: any,
  path: string,
  query: D,
  ctx?: GetServerSidePropsContext,
  clearTdeCookie?: boolean,
) => {
  const params = {
    prefix,
    path,
    query,
    ctx,
  };

  const config = await getReqConfig(ctx, undefined, clearTdeCookie);
  config.params = {
    ...config.params,
    ...query,
  };
  const allCookies = cookies(ctx || {});

  const { url, callback } = await getUrl({
    prefix,
    path,
    hostName: ctx && getHost(ctx),
    tdeProxy: allCookies[COOKIE_TDE_PROXY] === COOKIE_TDE_PROXY_TRUE,
    usePolaris: true,
    ctx,
  });
  const startTime = new Date().getTime();
  const reportPath = prefix + path;
  const response = await http
    .get<T>(url, { ...config, params: { ...config.params, ...query } })
    .then((res) => {
      // 错误需要上报一下
      // @ts-ignore
      if (!(res?.data && res.data.code === 0)) {
        reportHttpError(res.data, ctx, url, config, reportPath);
      }
      return res;
    })
    .catch((e) => {
      callback(false, 0, '-1');
      reportHttpError(e, ctx, url, config, reportPath);
      if (e?.response?.status === 401) {
        return e.response;
      }
      return Promise.reject(e);
    });
  const httpDuration = new Date().getTime() - startTime;
  if (ctx) {
    const reportLogData = `
      【method】: get,
      【url】: ${url},
      【httpDuration】:${httpDuration},
      【status】: ${JSON.stringify(response.status)},
      【response】: ${JSON.stringify({ ...response.data, data: {} })}`;
    // 上报日志需要脱敏
    // 【request】: ${JSON.stringify(query)},
    // 【params】:${JSON.stringify(response?.config?.params || {})},
    // 【response】: ${JSON.stringify(response.data)}`;

    // 全量返回日志
    nodeAegisInfoAll(`Response : ${reportLogData}`, ctx);
  }
  callback(true, httpDuration, '0');

  if (!isServer) {
    // 检测签名不合法重新获取
    response.data = await resetTicket(params, response, 'get');
  } else if (response.status !== 200) {
    // 服务端错误。
    return Promise.reject(response);
  }
  return response.data as T;
};

function reportHttpError(
  e: any,
  ctx: ApiOrSsrReqContext | undefined,
  url: string,
  config: AxiosRequestConfig,
  reportPath: string,
) {
  const msg = `CGI Request Error: [url]: ${url}, [data]: ${JSON.stringify(
    config,
  )}, [error info]: ${e}`;
  if (isServer && ctx) {
    nodeAegisReportError(msg, ctx);
    // 统计返回码错误数量
    nodeAegisReportEvent(`ServerError_${reportPath}`, ctx);
  } else if (window.nextAegis) {
    window.nextAegis.report(msg, e);
    window.nextAegis.reportEvent(`ServerError_${reportPath}`);
  }
}
// export default http;

export default {
  get,
  post,
};
